import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Eye, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "../../../components/ui/button/index.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu/index.js";
import { AlertDialog, AlertDialogDescription, AlertDialogTitle } from "../../../components/ui/alert-dialog/index.js";
import {
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts-dialog-presentation.js";
import { ContactsModalContent } from "../ContactsModalContent.js";

export function ContactRowActionsMenu({
  contactId,
  displayName,
  busy,
  onView,
  onCopyId,
  onDelete,
}: {
  contactId: number;
  displayName: string;
  busy?: boolean;
  onView: () => void;
  onCopyId: () => void | Promise<void>;
  onDelete: () => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <>
      <div
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-7 p-0"
              disabled={busy || deleting}
              aria-label={t("contacts.row.actions", { name: displayName })}
              data-testid={`contact-row-menu-${contactId}`}
            >
              <MoreHorizontal size={15} aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-44 p-1">
            <DropdownMenuItem
              className="py-1.5 text-[0.8125rem]"
              onSelect={() => onView()}
              data-testid={`contact-row-view-${contactId}`}
            >
              <Eye size={14} aria-hidden="true" />
              {t("contacts.row.view")}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="py-1.5 text-[0.8125rem]"
              onSelect={() => {
                void onCopyId();
              }}
              data-testid={`contact-row-copy-id-${contactId}`}
            >
              <Copy size={14} aria-hidden="true" />
              {t("contacts.row.copyId")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="py-1.5 text-[0.8125rem] text-destructive data-[highlighted]:bg-error-muted"
              onSelect={() => setConfirmDelete(true)}
              data-testid={`contact-row-delete-${contactId}`}
            >
              <Trash2 size={14} aria-hidden="true" />
              {t("contacts.row.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <ContactsModalContent>
          <div className={contactsModalHeaderClass}>
            <AlertDialogTitle>{t("contacts.detail.deleteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("contacts.detail.deleteConfirmBody")}</AlertDialogDescription>
          </div>
          <div className={contactsModalFooterClass}>
            <Button type="button" variant="ghost" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={deleting}
              onClick={() => {
                setDeleting(true);
                void onDelete()
                  .catch(() => undefined)
                  .finally(() => {
                    setDeleting(false);
                    setConfirmDelete(false);
                  });
              }}
            >
              {t("contacts.detail.delete")}
            </Button>
          </div>
        </ContactsModalContent>
      </AlertDialog>
    </>
  );
}
